import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, VStack, HStack, SimpleGrid, Flex,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Button, Input, NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  Badge, Spinner, Alert, AlertIcon, Select,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Card, CardBody, Switch, FormControl, FormLabel, Tooltip,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, useToast, Textarea,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const FEATURE_GROUP = {
  // Product Strategy
  'product-strategy': 'Product Strategy',
  'product-strategy/brief': 'Product Strategy',
  'product-strategy/market-scan': 'Product Strategy',
  'product-strategy/hypothesis': 'Product Strategy',
  'create-plan/brief-analysis': 'Plan Creation',
  'create-plan/brief-deconstruction': 'Plan Creation',
  'create-plan/market-scan-ai': 'Plan Creation',
  'create-plan/design-hypothesis': 'Plan Creation',
  'product-strategy-brief-deconstruction': 'Product Strategy',
  'product-strategy-market-scan': 'Product Strategy',
  'product-strategy-design-hypothesis': 'Product Strategy',
  'plan-strategy': 'Product Strategy',
  // Competitor Analysis
  'competitor-analysis': 'Competitor Analysis',
  'competitor-analysis/market': 'Competitor Analysis',
  'competitor-analysis/opportunity': 'Competitor Analysis',
  'competitor-analysis/report': 'Competitor Analysis',
  'autonomous-research-planning': 'Competitor Analysis',
  'competitor-market-intelligence': 'Competitor Analysis',
  'competitor-opportunity-analysis': 'Competitor Analysis',
  'competitor-report-synthesis': 'Competitor Analysis',
  // Action Plan
  'action-plan': 'Action Plan',
  'action-plan/research': 'Action Plan',
  'action-plan/programs': 'Action Plan',
  'autonomous-program-generation': 'Action Plan',
  'actions': 'Agent Tasks',
  // Documents
  'document': 'Documents',
  'docs': 'Documents',
  // Pulse
  'pulse': 'Pulse',
  'pulse-nudge': 'Pulse',
  // Chat
  'chat': 'Chat',
  'chat-synthesis': 'Chat',
  // Learnings
  'learning-eval': 'Learnings',
  'brief/analyze': 'Brief',
};

function groupTokensByFeature(rows) {
  const groups = {};
  rows.forEach(row => {
    const group = FEATURE_GROUP[row.feature] || row.feature || 'Other';
    if (!groups[group]) groups[group] = { totalTokens: 0, calls: 0, costUsd: 0 };
    groups[group].totalTokens += parseInt(row.totalTokens || 0, 10);
    groups[group].calls += parseInt(row.calls || 0, 10);
    groups[group].costUsd += parseFloat(row.costUsd || 0);
  });
  return groups;
}

const CREDIT_ACTION_GROUP = {
  'pulse': 'Pulse',
  'strategy-section': 'Product Strategy',
  'action-plan': 'Action Plan',
  'agent_task': 'Agent Tasks',
  'agent-task': 'Agent Tasks',
  'mcp-call': 'MCP Calls',
  'document': 'Documents',
  'competitor-analysis': 'Competitor Analysis',
  'learning-eval': 'Learnings',
};

const currentMonthKey = () => new Date().toISOString().slice(0, 7);

function addMonths(date, amount) {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount);
  return next;
}

function monthKey(date) {
  return date.toISOString().slice(0, 7);
}

function monthLabel(key) {
  return new Date(`${key}-01T00:00:00`).toLocaleDateString('en-NZ', {
    month: 'long',
    year: 'numeric',
  });
}

function buildPeriodOptions(monthCount = 12) {
  const now = new Date();
  return [
    { value: currentMonthKey(), label: 'Current month' },
    ...Array.from({ length: monthCount - 1 }, (_, index) => {
      const key = monthKey(addMonths(now, -(index + 1)));
      return { value: key, label: monthLabel(key) };
    }),
    { value: 'all', label: 'All time' },
  ];
}

function periodQuery(period) {
  return period === 'all' ? 'period=all' : `month=${encodeURIComponent(period)}`;
}

const AdminCreditManagement = () => {
  const [users, setUsers] = useState([]);
  const [costs, setCosts] = useState([]);
  const [tokenData, setTokenData] = useState([]);
  const [marginData, setMarginData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editedCosts, setEditedCosts] = useState({});
  const [editedGated, setEditedGated] = useState({});
  const [isSavingCost, setIsSavingCost] = useState({});
  const [opexPct, setOpexPct] = useState(0);
  const [targetMarginPct, setTargetMarginPct] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [creditPeriod, setCreditPeriod] = useState(currentMonthKey());
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const toast = useToast();

  const {
    isOpen: isUsageOpen,
    onOpen: onUsageOpen,
    onClose: onUsageClose,
  } = useDisclosure();
  const [usageUser, setUsageUser] = useState(null);
  const [usageDetail, setUsageDetail] = useState(null);
  const [isUsageLoading, setIsUsageLoading] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState(currentMonthKey());

  // Edit credits modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [editMonthlyBalance, setEditMonthlyBalance] = useState(0);
  const [editTopups, setEditTopups] = useState(0);
  const [adjustNotes, setAdjustNotes] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [usersRes, costsRes, tokenRes, marginRes, configRes] = await Promise.all([
        fetchWithAuth(`/api/credits/admin/users?${periodQuery(creditPeriod)}&include_admins=${includeAdmins}`),
        fetchWithAuth('/api/credits/costs'),
        fetchWithAuth('/api/admin/token-usage/by-feature?period=all'),
        fetchWithAuth('/api/credits/admin/margin'),
        fetchWithAuth('/api/credits/admin/pricing-config'),
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (marginRes.ok) setMarginData(await marginRes.json());
      if (costsRes.ok) {
        const c = await costsRes.json();
        setCosts(c);
        const initCosts = {};
        const initGated = {};
        c.forEach(row => {
          initCosts[row.action_type] = row.credits;
          initGated[row.action_type] = row.is_gated !== false; // default true
        });
        setEditedCosts(initCosts);
        setEditedGated(initGated);
      }
      if (tokenRes.ok) setTokenData(await tokenRes.json());
      if (configRes.ok) {
        const cfg = await configRes.json();
        setOpexPct(cfg.opexPct || 0);
        setTargetMarginPct(cfg.targetMarginPct || 0);
      }
    } catch (err) {
      console.error('Error loading credit management data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [creditPeriod, includeAdmins]);

  const savePricingConfig = async () => {
    try {
      const res = await fetchWithAuth('/api/credits/admin/pricing-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ opexPct, targetMarginPct }),
      });
      if (res.ok) {
        toast({ title: 'Pricing config saved', status: 'success', duration: 2000, isClosable: true });
      }
    } catch {
      toast({ title: 'Failed to save config', status: 'error', duration: 3000, isClosable: true });
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCostSave = async (actionType) => {
    setIsSavingCost(prev => ({ ...prev, [actionType]: true }));
    try {
      const newCredits = editedCosts[actionType];
      const newGated = editedGated[actionType];
      const res = await fetchWithAuth(`/api/credits/costs/${encodeURIComponent(actionType)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits: newCredits, is_gated: newGated }),
      });
      if (res.ok) {
        toast({ title: 'Credit cost updated', status: 'success', duration: 2000, isClosable: true });
        setCosts(prev => prev.map(c =>
          c.action_type === actionType ? { ...c, credits: newCredits, is_gated: newGated } : c
        ));
      } else {
        toast({ title: 'Failed to save', status: 'error', duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: 'Error saving', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsSavingCost(prev => ({ ...prev, [actionType]: false }));
    }
  };

  const openAdjust = (user) => {
    setAdjustTarget(user);
    setEditMonthlyBalance(user.credits_monthly_balance ?? 0);
    setEditTopups(user.credits_purchased_balance ?? 0);
    setAdjustNotes('');
    onOpen();
  };

  const loadUsageDetail = useCallback(async (user, period) => {
    if (!user?.id) return;
    setIsUsageLoading(true);
    try {
      const res = await fetchWithAuth(`/api/credits/admin/users/${user.id}/usage?${periodQuery(period)}`);
      const data = await res.json();
      if (res.ok) {
        setUsageDetail(data);
      } else {
        toast({ title: data.error || 'Failed to load usage detail', status: 'error', duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: 'Failed to load usage detail', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsUsageLoading(false);
    }
  }, [toast]);

  const openUsageDetail = async (user) => {
    setUsageUser(user);
    setUsagePeriod(creditPeriod);
    setUsageDetail(null);
    onUsageOpen();
  };

  useEffect(() => {
    if (isUsageOpen && usageUser) {
      loadUsageDetail(usageUser, usagePeriod);
    }
  }, [isUsageOpen, usagePeriod, usageUser, loadUsageDetail]);

  const handleAdjust = async () => {
    if (!adjustNotes.trim()) {
      toast({ title: 'Reason is required', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setIsAdjusting(true);
    try {
      const res = await fetchWithAuth('/api/credits/admin/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: adjustTarget.id,
          monthlyBalance: editMonthlyBalance,
          purchasedBalance: editTopups,
          notes: adjustNotes,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: 'Credits updated', status: 'success', duration: 2000, isClosable: true });
        onClose();
        fetchData();
      } else {
        toast({ title: data.error || 'Failed to update', status: 'error', duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: 'Error updating credits', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsAdjusting(false);
    }
  };

  const filteredUsers = users
    .filter(u =>
      !search || u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const usedA = Math.max(0, (a.credits_monthly_allowance ?? 30) - (a.credits_monthly_balance ?? 0));
      const usedB = Math.max(0, (b.credits_monthly_allowance ?? 30) - (b.credits_monthly_balance ?? 0));
      const pctA = (a.credits_monthly_allowance ?? 30) > 0 ? usedA / (a.credits_monthly_allowance ?? 30) : 0;
      const pctB = (b.credits_monthly_allowance ?? 30) > 0 ? usedB / (b.credits_monthly_allowance ?? 30) : 0;
      const maxSpent = Math.max(...users.map(u => parseInt(u.period_credits_spent ?? u.total_credits_spent ?? 0, 10)), 1);
      const spentA = parseInt(a.period_credits_spent ?? a.total_credits_spent ?? 0, 10) / maxSpent;
      const spentB = parseInt(b.period_credits_spent ?? b.total_credits_spent ?? 0, 10) / maxSpent;
      return (pctB * 0.6 + spentB * 0.4) - (pctA * 0.6 + spentA * 0.4);
    });

  const tokenGroups = groupTokensByFeature(tokenData);
  const USD_TO_NZD = 1.65;

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
        <Text mt={2} color="gray.500">Loading credit management...</Text>
      </Box>
    );
  }

  // Component-level pricing calculations — used by both the widget and the table rows
  const avgAiCostPerCredit = marginData?.avgAiCostPerCreditNzd ?? null;
  // Opex is a % on top of AI cost; margin is then applied on the total
  const pricingBase = (avgAiCostPerCredit || 0) * (1 + opexPct / 100);
  const suggestedPricePerCredit = pricingBase > 0 ? pricingBase * (1 + targetMarginPct / 100) : null;
  const formatNzd = (usd) => `NZ$${(parseFloat(usd || 0) * USD_TO_NZD).toFixed(2)}`;
  const formatNzdValue = (nzd, digits = 2) => `NZ$${parseFloat(nzd || 0).toFixed(digits)}`;
  const formatDate = (value) => value ? new Date(value).toLocaleString('en-NZ') : '—';
  const periodOptions = buildPeriodOptions();
  const selectedPeriodLabel = creditPeriod === 'all' ? 'All time' : monthLabel(creditPeriod);
  const totalMonthlyAllowance = users.reduce((sum, user) => sum + parseInt(user.credits_monthly_allowance || 0, 10), 0);
  const totalMonthlyLeft = users.reduce((sum, user) => sum + parseInt(user.credits_monthly_balance || 0, 10), 0);
  const totalTopupsLeft = users.reduce((sum, user) => sum + parseInt(user.credits_purchased_balance || 0, 10), 0);
  const totalPeriodTopupCredits = users.reduce((sum, user) => sum + parseInt(user.period_topup_credits || 0, 10), 0);
  const totalPeriodCredits = users.reduce((sum, user) => sum + parseInt(user.period_credits_spent ?? user.total_credits_spent ?? 0, 10), 0);
  const totalPeriodCalls = users.reduce((sum, user) => sum + parseInt(user.period_ai_calls ?? user.total_ai_calls ?? 0, 10), 0);
  const totalPeriodTokens = users.reduce((sum, user) => sum + parseInt(user.period_tokens_used ?? user.total_tokens_used ?? 0, 10), 0);
  const totalPeriodCostUsd = users.reduce((sum, user) => sum + parseFloat(user.period_ai_cost_usd_total ?? user.ai_cost_usd_total ?? 0), 0);
  const totalPeriodCostNzd = totalPeriodCostUsd * USD_TO_NZD;
  // Real revenue: actual cash collected from Stripe (subscriptions + top-ups), per user, for the period
  const totalRevenueNzd = users.reduce((sum, user) => sum + parseFloat(user.period_revenue_nzd || 0), 0);
  const totalGrossMarginNzd = totalRevenueNzd - totalPeriodCostNzd;
  // Billed value: retail value of credits spent + top-up credits purchased in the period (× Stripe list price).
  const retailPricePerCreditNzd = marginData?.listPricePerCreditNzd ?? null;
  const totalBilledValueNzd = retailPricePerCreditNzd != null ? (totalPeriodCredits + totalPeriodTopupCredits) * retailPricePerCreditNzd : 0;
  const totalValueMarginNzd = totalBilledValueNzd - totalPeriodCostNzd;
  const usersAtLimit = users.filter(user => parseInt(user.credits_monthly_balance || 0, 10) <= 0).length;
  const topPeriodUsers = [...users]
    .sort((a, b) => parseInt(b.period_credits_spent ?? b.total_credits_spent ?? 0, 10) - parseInt(a.period_credits_spent ?? a.total_credits_spent ?? 0, 10))
    .slice(0, 5);

  return (
    <Box p={6} w="100%">
      <Heading size="md" mb={2}>Credit Management</Heading>
      <Text color="gray.500" mb={6} fontSize="sm">
        Manage user credit balances and configure credit costs per action.
      </Text>

      <Tabs variant="line" colorScheme="brand" index={tabIndex} onChange={setTabIndex}>
        <TabList mb={4}>
          <Tab>Overview</Tab>
          <Tab>User Balances</Tab>
          <Tab>Credit Costs</Tab>
        </TabList>
        <TabPanels>
          {/* ── Overview Tab ─────────────────────────────────────────── */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                <Box>
                  <Heading size="sm">Credit Overview</Heading>
                  <Text fontSize="sm" color="gray.500">Usage, balance, and AI cost for {selectedPeriodLabel.toLowerCase()}.</Text>
                </Box>
                <Select
                  size="sm"
                  maxW="220px"
                  value={creditPeriod}
                  onChange={event => setCreditPeriod(event.target.value)}
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="credits-overview-include-admins" mb={0} fontSize="sm" color="gray.600" mr={2}>
                    Include admins
                  </FormLabel>
                  <Switch
                    id="credits-overview-include-admins"
                    size="sm"
                    isChecked={includeAdmins}
                    onChange={event => setIncludeAdmins(event.target.checked)}
                    colorScheme="purple"
                  />
                </FormControl>
              </Flex>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Credits Spent</Text>
                  <Text fontSize="2xl" fontWeight="700">{totalPeriodCredits.toLocaleString()}</Text>
                  <Text fontSize="xs" color="gray.400">{selectedPeriodLabel}</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Revenue</Text>
                  <Text fontSize="2xl" fontWeight="700">{formatNzdValue(totalRevenueNzd)}</Text>
                  <Text fontSize="xs" color="gray.400">Actual Stripe cash collected</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Billed Value</Text>
                  <Text fontSize="2xl" fontWeight="700">{formatNzdValue(totalBilledValueNzd)}</Text>
                  <Text fontSize="xs" color="gray.400">{retailPricePerCreditNzd != null ? `(Spent + top-ups) × NZ$${retailPricePerCreditNzd.toFixed(2)}/credit` : 'Credits + top-ups × list price'}</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">AI Cost</Text>
                  <Text fontSize="2xl" fontWeight="700">{formatNzd(totalPeriodCostUsd)}</Text>
                  <Text fontSize="xs" color="gray.400">Actual AI token cost</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Cash Margin</Text>
                  <Text fontSize="2xl" fontWeight="700" color={totalGrossMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                    {`${totalGrossMarginNzd >= 0 ? '+' : ''}${formatNzdValue(totalGrossMarginNzd)}`}
                  </Text>
                  <Text fontSize="xs" color="gray.400">Revenue minus AI cost</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Value Margin</Text>
                  <Text fontSize="2xl" fontWeight="700" color={totalValueMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                    {`${totalValueMarginNzd >= 0 ? '+' : ''}${formatNzdValue(totalValueMarginNzd)}`}
                  </Text>
                  <Text fontSize="xs" color="gray.400">Billed value minus AI cost</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">AI Calls</Text>
                  <Text fontSize="2xl" fontWeight="700">{totalPeriodCalls.toLocaleString()}</Text>
                  <Text fontSize="xs" color="gray.400">{totalPeriodTokens > 0 ? `${(totalPeriodTokens / 1000).toFixed(1)}k tokens` : 'No tokens'}</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Users At Limit</Text>
                  <Text fontSize="2xl" fontWeight="700" color={usersAtLimit > 0 ? 'red.500' : 'gray.800'}>{usersAtLimit}</Text>
                  <Text fontSize="xs" color="gray.400">{users.length.toLocaleString()} users tracked</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Monthly Left</Text>
                  <Text fontSize="2xl" fontWeight="700">{totalMonthlyLeft.toLocaleString()}</Text>
                  <Text fontSize="xs" color="gray.400">{totalMonthlyAllowance.toLocaleString()} allocated</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Top-ups Left</Text>
                  <Text fontSize="2xl" fontWeight="700" color={totalTopupsLeft > 0 ? 'green.600' : 'gray.800'}>{totalTopupsLeft.toLocaleString()}</Text>
                  <Text fontSize="xs" color="gray.400">Purchased credits</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">Top-ups Bought</Text>
                  <Text fontSize="2xl" fontWeight="700" color={totalPeriodTopupCredits > 0 ? 'green.600' : 'gray.800'}>{totalPeriodTopupCredits.toLocaleString()}</Text>
                  <Text fontSize="xs" color="gray.400">{selectedPeriodLabel}</Text>
                </CardBody></Card>
                <Card variant="outline"><CardBody>
                  <Text fontSize="xs" color="gray.500">AI Cost / Credit</Text>
                  <Text fontSize="2xl" fontWeight="700">{totalPeriodCredits > 0 ? `NZ$${((totalPeriodCostUsd * USD_TO_NZD) / totalPeriodCredits).toFixed(2)}` : '—'}</Text>
                  <Text fontSize="xs" color="gray.400">Selected period</Text>
                </CardBody></Card>
                {marginData?.monthlyTrend && (() => {
                  const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  // Build the latest 6 months, filling any month with no spend as 0
                  const byMonth = Object.fromEntries(marginData.monthlyTrend.map(d => [d.month, d.credits]));
                  const now = new Date();
                  const months = [];
                  for (let i = 5; i >= 0; i--) {
                    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
                    months.push({ key, mIdx: dt.getMonth(), credits: byMonth[key] || 0 });
                  }
                  const maxCredits = Math.max(...months.map(d => d.credits), 1);
                  return (
                    <Card variant="outline"><CardBody>
                      <Text fontSize="xs" color="gray.500" mb={2}>Credits spent · 6mo</Text>
                      <Flex align="flex-end" justify="space-between" h="44px" gap={1}>
                        {months.map(d => {
                          const pct = Math.round((d.credits / maxCredits) * 100);
                          return (
                            <Tooltip key={d.key} label={`${MONTH_ABBR[d.mIdx]}: ${d.credits.toLocaleString()} credits`} hasArrow placement="top">
                              <Box flex={1} h={`${d.credits > 0 ? Math.max(pct, 3) : 0}%`} minH={d.credits > 0 ? '2px' : '0'} bg="brand.400" borderRadius="sm" transition="background-color 0.15s" _hover={{ bg: 'brand.500' }} />
                            </Tooltip>
                          );
                        })}
                      </Flex>
                      <Flex justify="space-between" gap={1} mt={1}>
                        {months.map(d => (
                          <Text key={d.key} flex={1} textAlign="center" fontSize="9px" color="gray.400">{MONTH_ABBR[d.mIdx]}</Text>
                        ))}
                      </Flex>
                    </CardBody></Card>
                  );
                })()}
              </SimpleGrid>

              <Card variant="outline">
                <CardBody>
                  <Heading size="sm" mb={3}>Top Credit Users</Heading>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>User</Th>
                        <Th>Plan</Th>
                        <Th isNumeric title="Allowance credits remaining right now / total allowance. Current snapshot — not the selected period. Usage is the Credits Spent column.">Balance</Th>
                        <Th isNumeric>Credits Spent</Th>
                        <Th isNumeric title="Top-up credits purchased in the selected period.">Top-ups</Th>
                        <Th isNumeric title="Retail value of credits spent + top-up credits purchased ((spent + top-ups) × NZ$ list price per credit).">Billed</Th>
                        <Th isNumeric title="Actual Stripe cash collected after discounts and refunds.">Revenue</Th>
                        <Th isNumeric>AI Calls</Th>
                        <Th isNumeric>AI Cost</Th>
                        <Th isNumeric>Cash Margin</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {topPeriodUsers.map(user => {
                        const spent = parseInt(user.period_credits_spent ?? user.total_credits_spent ?? 0, 10);
                        const allowance = user.credits_monthly_allowance ?? 30;
                        const cycleSpent = parseInt(user.cycle_credits_spent ?? 0, 10);
                        const monthlyLeft = Math.max(0, allowance - Math.min(allowance, cycleSpent));
                        const leftPct = allowance > 0 ? (monthlyLeft / allowance) * 100 : 100;
                        const calls = parseInt(user.period_ai_calls ?? user.total_ai_calls ?? 0, 10);
                        const cost = parseFloat(user.period_ai_cost_usd_total ?? user.ai_cost_usd_total ?? 0);
                        const revenueNzd = parseFloat(user.period_revenue_nzd || 0);
                        const grossMarginNzd = revenueNzd - (cost * USD_TO_NZD);
                        const topupCredits = parseInt(user.period_topup_credits || 0, 10);
                        const billedValueNzd = retailPricePerCreditNzd != null ? (spent + topupCredits) * retailPricePerCreditNzd : 0;
                        return (
                          <Tr key={user.id} cursor="pointer" _hover={{ bg: 'gray.50' }} onClick={() => openUsageDetail(user)}>
                            <Td>
                              <Text fontWeight="600" fontSize="sm">{user.first_name} {user.last_name}</Text>
                              <Text fontSize="xs" color="gray.500">{user.email}</Text>
                            </Td>
                            <Td><Badge colorScheme={user.plan_type === 'pro' ? 'brand' : 'gray'}>{user.plan_type || 'free'}</Badge></Td>
                            <Td isNumeric whiteSpace="nowrap">
                              <Text as="span" fontWeight="600" color={leftPct <= 10 ? 'red.500' : leftPct <= 30 ? 'orange.400' : 'gray.700'}>{monthlyLeft}</Text>
                              <Text as="span" color="gray.400"> / {allowance}</Text>
                            </Td>
                            <Td isNumeric fontWeight="700">{spent.toLocaleString()}</Td>
                            <Td isNumeric color={topupCredits > 0 ? 'green.600' : 'gray.300'}>{topupCredits > 0 ? topupCredits.toLocaleString() : '—'}</Td>
                            <Td isNumeric color={billedValueNzd > 0 ? 'gray.700' : 'gray.300'}>{billedValueNzd > 0 ? formatNzdValue(billedValueNzd) : '—'}</Td>
                            <Td isNumeric color={revenueNzd > 0 ? 'gray.700' : 'gray.300'}>{revenueNzd > 0 ? formatNzdValue(revenueNzd) : '—'}</Td>
                            <Td isNumeric>{calls > 0 ? calls.toLocaleString() : '—'}</Td>
                            <Td isNumeric>{cost > 0 ? formatNzd(cost) : '—'}</Td>
                            <Td isNumeric color={grossMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                              {(revenueNzd > 0 || cost > 0) ? `${grossMarginNzd >= 0 ? '+' : ''}${formatNzdValue(grossMarginNzd)}` : '—'}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* ── User Balances Tab ─────────────────────────────────────── */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                <Input
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  maxW="360px"
                  size="sm"
                />
                <Select
                  size="sm"
                  maxW="220px"
                  value={creditPeriod}
                  onChange={event => setCreditPeriod(event.target.value)}
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="credits-users-include-admins" mb={0} fontSize="sm" color="gray.600" mr={2}>
                    Include admins
                  </FormLabel>
                  <Switch
                    id="credits-users-include-admins"
                    size="sm"
                    isChecked={includeAdmins}
                    onChange={event => setIncludeAdmins(event.target.checked)}
                    colorScheme="purple"
                  />
                </FormControl>
              </Flex>
              <TableContainer w="100%" overflowX="auto">
                <Table size="sm" variant="simple" sx={{ 'th, td': { px: 2, py: 1.5 } }}>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>User</Th>
                      <Th isNumeric title="Allowance credits remaining right now / total allowance. Current snapshot — not the selected period. Usage is the Spent column.">Balance</Th>
                      <Th isNumeric title="Top-up credits purchased in the selected period.">Top-ups</Th>
                      <Th isNumeric>Spent</Th>
                      <Th isNumeric title="Retail value of credits spent + top-up credits purchased ((spent + top-ups) × NZ$ list price per credit).">Billed</Th>
                      <Th isNumeric title="Actual cash collected from this user in the period (Stripe subscriptions + top-ups). Free users with no purchases show nothing.">Revenue</Th>
                      <Th isNumeric title="Actual AI token cost for all recorded AI usage in the selected period.">AI cost</Th>
                      <Th isNumeric title="Actual revenue minus AI token cost.">Margin</Th>
                      <Th isNumeric title="AI calls and tokens used in the period.">AI usage</Th>
                      <Th>Resets</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredUsers.map(user => {
                      const allowance = user.credits_monthly_allowance ?? 30;
                      const cycleSpent = parseInt(user.cycle_credits_spent ?? 0, 10);
                      const monthlyLeft = Math.max(0, allowance - Math.min(allowance, cycleSpent));
                      const topupsBought = parseInt(user.period_topup_credits || 0, 10);
                      const leftPct = allowance > 0 ? (monthlyLeft / allowance) * 100 : 100;
                      const totalSpent = parseInt(user.period_credits_spent ?? user.total_credits_spent ?? 0, 10);
                      const aiCalls = parseInt(user.period_ai_calls ?? user.total_ai_calls ?? 0, 10);
                      const tokensUsed = parseInt(user.period_tokens_used ?? user.total_tokens_used ?? 0, 10);
                      const isPro = user.plan_type === 'pro';

                      // Token-based AI cost for all recorded AI usage
                      const aiCostUsdTotal = parseFloat(user.period_ai_cost_usd_total ?? user.ai_cost_usd_total ?? 0);
                      const aiCostNzd = aiCostUsdTotal * 1.65;
                      const hasAiCost = aiCostNzd > 0.0001;
                      const revenueNzd = parseFloat(user.period_revenue_nzd || 0);
                      const grossMarginNzd = revenueNzd - aiCostNzd;
                      const billedValueNzd = retailPricePerCreditNzd != null ? (totalSpent + topupsBought) * retailPricePerCreditNzd : 0;

                      return (
                        <Tr
                          key={user.id}
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => openUsageDetail(user)}
                        >
                          <Td>
                            <VStack align="start" spacing={0}>
                              <HStack spacing={2}>
                                <Text fontWeight="medium" fontSize="sm" noOfLines={1}>{user.first_name} {user.last_name}</Text>
                                <Badge colorScheme={isPro ? 'brand' : 'gray'} fontSize="9px">{user.plan_type || 'free'}</Badge>
                                {user.is_admin && <Badge colorScheme="purple" fontSize="9px">Admin</Badge>}
                              </HStack>
                              <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="200px">{user.email}</Text>
                            </VStack>
                          </Td>
                          <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                            <Text as="span" fontWeight="semibold" color={leftPct <= 10 ? 'red.500' : leftPct <= 30 ? 'orange.400' : 'gray.700'}>{monthlyLeft}</Text>
                            <Text as="span" color="gray.400"> / {allowance}</Text>
                          </Td>
                          <Td isNumeric fontSize="sm" color={topupsBought > 0 ? 'green.600' : 'gray.400'}>{topupsBought > 0 ? topupsBought.toLocaleString() : '—'}</Td>
                          <Td isNumeric fontSize="sm" fontWeight="semibold" color="gray.700">{totalSpent > 0 ? totalSpent.toLocaleString() : '—'}</Td>
                          <Td isNumeric fontSize="sm" color={billedValueNzd > 0 ? 'gray.700' : 'gray.300'}>
                            {billedValueNzd > 0 ? formatNzdValue(billedValueNzd) : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" fontWeight="semibold" color={revenueNzd > 0 ? 'green.600' : 'gray.300'}>
                            {revenueNzd > 0 ? formatNzdValue(revenueNzd) : '—'}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {hasAiCost ? (
                              <Text
                                color="gray.700"
                                whiteSpace="nowrap"
                                title="Actual AI token cost"
                              >
                                NZ${aiCostNzd.toFixed(2)}
                              </Text>
                            ) : (
                              <Text color="gray.300">—</Text>
                            )}
                          </Td>
                          <Td isNumeric fontSize="xs" fontWeight="semibold" whiteSpace="nowrap" color={(revenueNzd > 0 || hasAiCost) ? grossMarginNzd >= 0 ? 'green.600' : 'red.500' : 'gray.300'}>
                            {(revenueNzd > 0 || hasAiCost) ? `${grossMarginNzd >= 0 ? '+' : ''}${formatNzdValue(grossMarginNzd)}` : '—'}
                          </Td>
                          <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                            {aiCalls > 0 ? (
                              <>
                                <Text as="span" color="gray.700">{aiCalls.toLocaleString()}</Text>
                                {tokensUsed > 0 && <Text as="span" color="gray.400"> · {(tokensUsed / 1000).toFixed(0)}k</Text>}
                              </>
                            ) : <Text as="span" color="gray.300">—</Text>}
                          </Td>
                          <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
                            {user.credits_reset_at ? new Date(user.credits_reset_at).toLocaleDateString('en-NZ') : '—'}
                          </Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="brand"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAdjust(user);
                              }}
                            >
                              Edit
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <Tr>
                        <Td colSpan={11}>
                          <Text color="gray.400" textAlign="center" py={4}>No users found</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </TabPanel>

          {/* ── Credit Costs Tab ──────────────────────────────────────── */}
          <TabPanel px={0}>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Edit credit costs per action type. Changes take effect within 5 minutes (cache).
                Token usage data helps calibrate costs against actual AI spend.
              </Text>

              {/* Margin summary widgets */}
              {marginData && (() => {
                const creditsThisMonth = marginData.totals?.thisMonth || 0;
                const creditsLastMonth = marginData.totals?.lastMonth || 0;
                const momPct = creditsLastMonth > 0
                  ? +((creditsThisMonth - creditsLastMonth) / creditsLastMonth * 100).toFixed(0)
                  : null;
                const featureCount = (marginData.perActionBreakdown || []).filter(r => r.calls > 0).length;
                // Use component-level computed values
                const suggestedPrice = suggestedPricePerCredit;

                return (
                  <VStack align="stretch" spacing={4} mb={4}>
                    <Card variant="outline" size="sm">
                      <CardBody>
                        <Text fontSize="xs" color="gray.500" fontWeight="600" mb={3} textTransform="uppercase" letterSpacing="wide">
                          1 Pabs Credit — Cost Build-up
                        </Text>
                        <Flex align="center" gap={3} flexWrap="wrap">
                          {/* AI Cost — from data */}
                          <Box textAlign="center" flex="1" minW="110px" p={3} bg="blue.50" borderRadius="md">
                            <Text fontSize="xs" color="gray.500" mb={1}>AI Cost</Text>
                            <Text fontSize="xl" fontWeight="700" color={avgAiCostPerCredit != null ? 'blue.700' : 'gray.300'}>
                              {avgAiCostPerCredit != null ? `NZ$${avgAiCostPerCredit.toFixed(2)}` : '—'}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {featureCount > 0 ? `avg across ${featureCount} features` : 'No usage data yet'}
                            </Text>
                          </Box>

                          <Text fontSize="lg" color="gray.400">+</Text>

                          {/* Opex — editable % */}
                          <Box textAlign="center" flex="1" minW="110px" p={3} bg={opexPct > 0 ? 'orange.50' : 'gray.50'} borderRadius="md">
                            <Text fontSize="xs" color="gray.500" mb={1}>Opex (%)</Text>
                            <NumberInput
                              size="sm"
                              value={opexPct}
                              onChange={(_, val) => setOpexPct(isNaN(val) ? 0 : val)}
                              min={0} max={500} step={5} precision={1}
                            >
                              <NumberInputField textAlign="center" fontWeight="700" fontSize="lg" color={opexPct > 0 ? 'orange.700' : 'gray.400'} />
                            </NumberInput>
                            <Text fontSize="xs" color="gray.400">% on top of AI cost</Text>
                          </Box>

                          <Text fontSize="lg" color="gray.400">+</Text>

                          {/* Margin — editable */}
                          <Box textAlign="center" flex="1" minW="110px" p={3} bg={targetMarginPct > 0 ? 'purple.50' : 'gray.50'} borderRadius="md">
                            <Text fontSize="xs" color="gray.500" mb={1}>Target Margin (%)</Text>
                            <NumberInput
                              size="sm"
                              value={targetMarginPct}
                              onChange={(_, val) => setTargetMarginPct(isNaN(val) ? 0 : val)}
                              min={0} max={99} step={5} precision={1}
                            >
                              <NumberInputField textAlign="center" fontWeight="700" fontSize="lg" color={targetMarginPct > 0 ? 'purple.700' : 'gray.400'} />
                            </NumberInput>
                            <Text fontSize="xs" color="gray.400">% markup on AI + opex</Text>
                          </Box>

                          <Text fontSize="lg" color="gray.400">=</Text>

                          {/* Suggested price — calculated */}
                          <Box textAlign="center" flex="1" minW="120px" p={3} bg={suggestedPrice ? 'green.50' : 'gray.50'} borderRadius="md" borderWidth="1px" borderColor={suggestedPrice ? 'green.200' : 'gray.200'}>
                            <Text fontSize="xs" color="gray.500" mb={1}>Suggested Price</Text>
                            <Text fontSize="xl" fontWeight="700" color={suggestedPrice ? 'green.700' : 'gray.300'}>
                              {suggestedPrice ? `NZ$${suggestedPrice.toFixed(2)}` : '—'}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              {suggestedPrice ? `NZ$${suggestedPrice.toFixed(2)} rounded` : 'Set opex + margin above'}
                            </Text>
                          </Box>
                        </Flex>
                        <Flex justify="flex-end" mt={3}>
                          <Button size="sm" colorScheme="brand" onClick={savePricingConfig}>
                            Save Pricing Config
                          </Button>
                        </Flex>
                      </CardBody>
                    </Card>

                    {/* Credits this month */}
                    <Card variant="outline" size="sm" alignSelf="flex-start">
                      <CardBody>
                        <Text fontSize="xs" color="gray.500" mb={1}>Credits This Month</Text>
                        <Text fontSize="xl" fontWeight="700" color="gray.800">{creditsThisMonth.toLocaleString()}</Text>
                        <Text fontSize="xs" color={momPct === null ? 'gray.400' : momPct >= 0 ? 'green.500' : 'red.500'}>
                          {momPct !== null ? `${momPct >= 0 ? '+' : ''}${momPct}% vs last month` : `${creditsLastMonth} last month`}
                        </Text>
                      </CardBody>
                    </Card>
                  </VStack>
                );
              })()}
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Feature</Th>
                      <Th isNumeric>Credits</Th>
                      <Th title="Block action when credits run out. Off = track only.">Gated</Th>
                      <Th isNumeric>Avg Tokens</Th>
                      <Th isNumeric>AI Cost / Call</Th>
                      <Th isNumeric>AI Cost / Credit</Th>
                      <Th isNumeric title="credits × suggested price per credit">Revenue / Call</Th>
                      <Th isNumeric title="Revenue minus AI cost and opex">Net Margin / Call</Th>
                      <Th isNumeric title="Net margin as % of revenue">Margin %</Th>
                      <Th>Calibration</Th>
                      <Th isNumeric>Suggested Credits</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {costs.map(cost => {
                      const group = CREDIT_ACTION_GROUP[cost.action_type];
                      const tokenGroup = group ? tokenGroups[group] : null;
                      const avgTokens = tokenGroup && tokenGroup.calls > 0
                        ? Math.round(tokenGroup.totalTokens / tokenGroup.calls)
                        : null;

                      // From perActionBreakdown (backend-calculated)
                      const breakdown = (marginData?.perActionBreakdown || []).find(r => r.actionType === cost.action_type);
                      const aiCostPerCall = breakdown ? breakdown.avgCostPerCallNzd : null;

                      // Use the live-edited credit count so calibration reacts to changes
                      const currentVal = editedCosts[cost.action_type] ?? cost.credits;
                      const currentGated = editedGated[cost.action_type] ?? (cost.is_gated !== false);
                      const isDirty = currentVal !== cost.credits || currentGated !== (cost.is_gated !== false);

                      // Recalculate AI cost per credit using the current (possibly edited) credit count
                      const aiCostPerCreditRow = (aiCostPerCall != null && currentVal > 0)
                        ? aiCostPerCall / currentVal
                        : null;

                      // Margin columns — only computable when we have AI cost data AND a suggested price
                      const revenuePerCall = (suggestedPricePerCredit && currentVal > 0)
                        ? currentVal * suggestedPricePerCredit
                        : null;
                      const totalCostPerCall = (aiCostPerCall != null)
                        ? aiCostPerCall * (1 + opexPct / 100)
                        : null;
                      const netMarginNzd = (revenuePerCall != null && totalCostPerCall != null)
                        ? revenuePerCall - totalCostPerCall
                        : null;
                      const marginPct = (netMarginNzd != null && revenuePerCall > 0)
                        ? (netMarginNzd / revenuePerCall) * 100
                        : null;

                      // Calibration: use avgAiCostPerCredit as a fixed reference price so the target
                      // (which includes opex + margin) actually reacts when the sliders change.
                      // ratio = (credits × base AI price) / (what the feature costs to serve at target margin)
                      let calibration = null;
                      if (aiCostPerCall != null && avgAiCostPerCredit != null && avgAiCostPerCredit > 0) {
                        const revenueAtBase = currentVal * avgAiCostPerCredit;
                        const targetRevenue = aiCostPerCall * (1 + opexPct / 100) * (1 + targetMarginPct / 100);
                        const ratio = revenueAtBase / targetRevenue;
                        if (ratio < 0.7) calibration = { label: 'Under-priced', color: 'red' };
                        else if (ratio > 1.3) calibration = { label: 'Over-priced', color: 'orange' };
                        else calibration = { label: 'Calibrated', color: 'green' };
                      }

                      // Suggested credits: how many credits at the base AI price cover this feature's cost
                      // at the target opex + margin. Increases as margin/opex increase.
                      const suggestedCredits = (aiCostPerCall != null && avgAiCostPerCredit != null && avgAiCostPerCredit > 0)
                        ? Math.max(1, Math.ceil(aiCostPerCall * (1 + opexPct / 100) * (1 + targetMarginPct / 100) / avgAiCostPerCredit))
                        : null;
                      const suggestedDiffers = suggestedCredits !== null && suggestedCredits !== currentVal;

                      return (
                        <Tr key={cost.action_type}>
                          <Td>
                            <Text fontSize="sm" fontWeight="500">{cost.label}</Text>
                            <Text fontSize="xs" color="gray.400" fontFamily="mono">{cost.action_type}</Text>
                          </Td>
                          <Td isNumeric>
                            <NumberInput
                              min={1} max={100}
                              value={currentVal}
                              onChange={(_, val) => setEditedCosts(prev => ({ ...prev, [cost.action_type]: isNaN(val) ? cost.credits : val }))}
                              size="xs" maxW="80px" ml="auto"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <Switch
                              size="sm"
                              isChecked={currentGated}
                              onChange={e => setEditedGated(prev => ({ ...prev, [cost.action_type]: e.target.checked }))}
                              colorScheme="red"
                            />
                          </Td>
                          <Td isNumeric fontSize="sm" color="gray.600">
                            {avgTokens !== null ? avgTokens.toLocaleString() : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" color="blue.700">
                            {aiCostPerCall != null ? `NZ$${aiCostPerCall.toFixed(2)}` : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" color="blue.700" fontWeight="600">
                            {aiCostPerCreditRow != null ? `NZ$${aiCostPerCreditRow.toFixed(2)}` : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" color={revenuePerCall != null ? 'gray.700' : 'gray.300'}>
                            {revenuePerCall != null ? `NZ$${revenuePerCall.toFixed(2)}` : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" fontWeight="600"
                            color={netMarginNzd == null ? 'gray.300' : netMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                            {netMarginNzd != null ? `${netMarginNzd >= 0 ? '+' : ''}NZ$${netMarginNzd.toFixed(2)}` : '—'}
                          </Td>
                          <Td isNumeric fontSize="sm" fontWeight="600"
                            color={marginPct == null ? 'gray.300' : marginPct >= targetMarginPct ? 'green.600' : marginPct >= 0 ? 'orange.500' : 'red.500'}>
                            {marginPct != null ? `${marginPct >= 0 ? '+' : ''}${marginPct.toFixed(1)}%` : '—'}
                          </Td>
                          <Td>
                            {calibration
                              ? <Badge colorScheme={calibration.color} fontSize="xs">{calibration.label}</Badge>
                              : <Text fontSize="xs" color="gray.300">No data</Text>
                            }
                          </Td>
                          <Td isNumeric>
                            {suggestedCredits != null ? (
                              <HStack justify="flex-end" spacing={1}>
                                <Text fontSize="sm" fontWeight={suggestedDiffers ? '700' : '400'} color={suggestedDiffers ? 'orange.600' : 'gray.400'}>
                                  {suggestedCredits}
                                </Text>
                                {suggestedDiffers && (
                                  <Button
                                    size="xs" variant="outline" colorScheme="orange"
                                    onClick={() => setEditedCosts(prev => ({ ...prev, [cost.action_type]: suggestedCredits }))}
                                  >
                                    Apply
                                  </Button>
                                )}
                              </HStack>
                            ) : (
                              <Text fontSize="xs" color="gray.300">—</Text>
                            )}
                          </Td>
                          <Td>
                            <Button
                              size="xs" colorScheme="brand"
                              isDisabled={!isDirty}
                              isLoading={isSavingCost[cost.action_type]}
                              onClick={() => handleCostSave(cost.action_type)}
                            >
                              Save
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>

              {tokenData.length === 0 && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Token usage data not yet available. Run some workflows to see AI cost estimates here.
                </Alert>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Drawer isOpen={isUsageOpen} placement="right" size="xl" onClose={onUsageClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Flex justify="space-between" align="flex-start" gap={4} pr={8}>
              <Box>
                <Text fontSize="md">{usageUser?.first_name} {usageUser?.last_name}</Text>
                <Text fontSize="sm" color="gray.500" fontWeight="400">{usageUser?.email}</Text>
              </Box>
              <Select
                size="sm"
                maxW="180px"
                value={usagePeriod}
                onChange={event => setUsagePeriod(event.target.value)}
                fontWeight="400"
              >
                {periodOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </Flex>
          </DrawerHeader>
          <DrawerBody py={5}>
            {isUsageLoading && (
              <Box textAlign="center" py={10}>
                <Spinner />
                <Text mt={2} color="gray.500" fontSize="sm">Loading usage detail...</Text>
              </Box>
            )}

            {!isUsageLoading && usageDetail && (
              <VStack align="stretch" spacing={6}>
                {(() => {
                  const u = usageDetail.user || usageUser || {};
                  const allowance = parseInt(u.credits_monthly_allowance || 30, 10);
                  const cycleSpent = parseInt(u.cycle_credits_spent || 0, 10);
                  const monthlyLeft = Math.max(0, allowance - Math.min(allowance, cycleSpent));
                  const topups = parseInt(u.credits_purchased_balance || 0, 10);
                  const reserved = parseInt(u.credits_reserved || 0, 10);
                  const reservedTasks = parseInt(u.reserved_task_count || 0, 10);
                  const autonomousSpent = parseInt(u.autonomous_credits_spent || 0, 10);
                  const financial = usageDetail.financialSummary || {};
                  const totalSpent = (usageDetail.creditSummary || [])
                    .reduce((sum, row) => sum + parseInt(row.credits_spent || 0, 10), 0);
                  const totalTokens = (usageDetail.tokenSummary || [])
                    .reduce((sum, row) => sum + parseInt(row.total_tokens || 0, 10), 0);
                  const totalCalls = (usageDetail.tokenSummary || [])
                    .reduce((sum, row) => sum + parseInt(row.calls || 0, 10), 0);
                  const creditsSpent = financial.creditsSpent ?? totalSpent;
                  const topupCredits = parseInt(financial.topupCredits || 0, 10);
                  const billedValueNzd = financial.billedValueNzd;
                  const revenueNzd = parseFloat(financial.revenueNzd || 0);
                  const aiCostNzd = parseFloat(financial.aiCostNzdTotal || 0);
                  const cashMarginNzd = financial.cashMarginNzd ?? (revenueNzd - aiCostNzd);
                  const valueMarginNzd = financial.valueMarginNzd;

                  return (
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Credits spent</Text>
                        <Text fontSize="lg" fontWeight="700">{parseInt(creditsSpent || 0, 10).toLocaleString()}</Text>
                        <Text fontSize="xs" color="gray.400">{usagePeriod === 'all' ? 'All time' : monthLabel(usagePeriod)}</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Billed value</Text>
                        <Text fontSize="lg" fontWeight="700">{billedValueNzd != null ? formatNzdValue(billedValueNzd) : '—'}</Text>
                        <Text fontSize="xs" color="gray.400">Top-up value before coupons</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Top-ups bought</Text>
                        <Text fontSize="lg" fontWeight="700" color={topupCredits > 0 ? 'green.600' : 'gray.700'}>{topupCredits.toLocaleString()}</Text>
                        <Text fontSize="xs" color="gray.400">{usagePeriod === 'all' ? 'All time' : monthLabel(usagePeriod)}</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Revenue</Text>
                        <Text fontSize="lg" fontWeight="700" color={revenueNzd > 0 ? 'green.600' : 'gray.700'}>{formatNzdValue(revenueNzd)}</Text>
                        <Text fontSize="xs" color="gray.400">Actual Stripe cash</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">AI cost</Text>
                        <Text fontSize="lg" fontWeight="700">{formatNzdValue(aiCostNzd)}</Text>
                        <Text fontSize="xs" color="gray.400">Actual token cost</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Cash margin</Text>
                        <Text fontSize="lg" fontWeight="700" color={cashMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                          {`${cashMarginNzd >= 0 ? '+' : ''}${formatNzdValue(cashMarginNzd)}`}
                        </Text>
                        <Text fontSize="xs" color="gray.400">Revenue − AI cost</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Value margin</Text>
                        <Text fontSize="lg" fontWeight="700" color={valueMarginNzd == null ? 'gray.700' : valueMarginNzd >= 0 ? 'green.600' : 'red.500'}>
                          {valueMarginNzd != null ? `${valueMarginNzd >= 0 ? '+' : ''}${formatNzdValue(valueMarginNzd)}` : '—'}
                        </Text>
                        <Text fontSize="xs" color="gray.400">Billed value − AI cost</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">AI usage</Text>
                        <Text fontSize="lg" fontWeight="700">{(financial.totalCalls ?? totalCalls).toLocaleString()}</Text>
                        <Text fontSize="xs" color="gray.400">{(financial.totalTokens ?? totalTokens) > 0 ? `${((financial.totalTokens ?? totalTokens) / 1000).toFixed(1)}k tokens` : 'No tokens'}</Text>
                      </Box>
                      <Box bg="gray.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="gray.500">Balance (now)</Text>
                        <Text fontSize="lg" fontWeight="700">{monthlyLeft} + {topups}</Text>
                        <Text fontSize="xs" color="gray.400">
                          {Math.max(0, monthlyLeft + topups - reserved)} available after {reserved} reserved
                        </Text>
                      </Box>
                      <Box bg="purple.50" borderRadius="md" p={3}>
                        <Text fontSize="xs" color="purple.600">Autonomous work</Text>
                        <Text fontSize="lg" fontWeight="700">{autonomousSpent} spent · {reserved} reserved</Text>
                        <Text fontSize="xs" color="purple.500">{reservedTasks} scheduled task{reservedTasks === 1 ? '' : 's'} holding credits</Text>
                      </Box>
                    </SimpleGrid>
                  );
                })()}

                <Box>
                  <Heading size="sm" mb={3}>Credits By Action</Heading>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Action</Th>
                        <Th isNumeric>Runs</Th>
                        <Th isNumeric>Credits</Th>
                        <Th>Last</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(usageDetail.creditSummary || []).map(row => (
                        <Tr key={row.action_type}>
                          <Td>
                            <Text fontSize="sm" fontWeight="600">{CREDIT_ACTION_GROUP[row.action_type] || row.action_type}</Text>
                            <Text fontSize="xs" color="gray.400" fontFamily="mono">{row.action_type}</Text>
                          </Td>
                          <Td isNumeric>{parseInt(row.deductions || 0, 10)}</Td>
                          <Td isNumeric fontWeight="700">{parseInt(row.credits_spent || 0, 10)}</Td>
                          <Td fontSize="xs" color="gray.500">{formatDate(row.last_activity)}</Td>
                        </Tr>
                      ))}
                      {(usageDetail.creditSummary || []).length === 0 && (
                        <Tr><Td colSpan={4}><Text color="gray.400" textAlign="center" py={3}>No credit transactions</Text></Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>

                <Box>
                  <Heading size="sm" mb={3}>AI Usage By Feature</Heading>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Feature</Th>
                        <Th>Model</Th>
                        <Th isNumeric>Calls</Th>
                        <Th isNumeric>Tokens</Th>
                        <Th isNumeric>Cost</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(usageDetail.tokenSummary || []).map((row, idx) => (
                        <Tr key={`${row.feature}-${row.model}-${idx}`}>
                          <Td>
                            <Text fontSize="sm" fontWeight="600">{FEATURE_GROUP[row.feature] || row.feature || 'Other'}</Text>
                            <Text fontSize="xs" color="gray.400" fontFamily="mono">{row.feature || 'unknown'}</Text>
                          </Td>
                          <Td><Badge colorScheme="purple" fontSize="10px">{row.model || 'unknown'}</Badge></Td>
                          <Td isNumeric>{parseInt(row.calls || 0, 10)}</Td>
                          <Td isNumeric>{parseInt(row.total_tokens || 0, 10).toLocaleString()}</Td>
                          <Td isNumeric>{formatNzd(row.cost_usd)}</Td>
                        </Tr>
                      ))}
                      {(usageDetail.tokenSummary || []).length === 0 && (
                        <Tr><Td colSpan={5}><Text color="gray.400" textAlign="center" py={3}>No token usage recorded</Text></Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>

                <Box>
                  <Heading size="sm" mb={3}>Recent Credit Transactions</Heading>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>When</Th>
                        <Th>Action</Th>
                        <Th isNumeric>Amount</Th>
                        <Th>Notes</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(usageDetail.transactions || []).slice(0, 30).map(tx => (
                        <Tr key={tx.id}>
                          <Td fontSize="xs" color="gray.500">{formatDate(tx.created_at)}</Td>
                          <Td>
                            <HStack spacing={1.5} align="center" flexWrap="wrap">
                              <Text fontSize="sm">{CREDIT_ACTION_GROUP[tx.action_type] || tx.action_type}</Text>
                              {tx.agent_task_tag && (
                                <>
                                  <Text fontSize="xs" color="gray.400">›</Text>
                                  <Badge colorScheme="purple" fontSize="10px" textTransform="none">
                                    {tx.agent_task_tag}
                                  </Badge>
                                </>
                              )}
                            </HStack>
                            <Text
                              fontSize="xs"
                              color="gray.400"
                              noOfLines={2}
                              title={tx.agent_task_description || tx.agent_task_capability || tx.action_type}
                            >
                              {tx.agent_task_description || tx.agent_task_capability || tx.action_type}
                              {tx.agent_task_inferred ? ' (matched from task activity)' : ''}
                            </Text>
                          </Td>
                          <Td isNumeric fontWeight="700" color={parseInt(tx.amount || 0, 10) < 0 ? 'orange.600' : 'green.600'}>
                            {parseInt(tx.amount || 0, 10)}
                          </Td>
                          <Td fontSize="xs" color="gray.500">{tx.notes || tx.type || '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Box>
                  <Heading size="sm" mb={3}>Recent AI Calls</Heading>
                  <Table size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>When</Th>
                        <Th>Feature</Th>
                        <Th>Plan</Th>
                        <Th isNumeric>Tokens</Th>
                        <Th isNumeric>Cost</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(usageDetail.recentTokenUsage || []).slice(0, 30).map(row => (
                        <Tr key={row.id}>
                          <Td fontSize="xs" color="gray.500">{formatDate(row.created_at)}</Td>
                          <Td>
                            <Text fontSize="sm">{FEATURE_GROUP[row.feature] || row.feature || 'Other'}</Text>
                            <Text fontSize="xs" color="gray.400" fontFamily="mono">{row.feature || 'unknown'}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{row.plan_name || (row.plan_id ? `Plan #${row.plan_id}` : 'Unattached')}</Text>
                            {row.workflow_id && <Text fontSize="xs" color="gray.400" fontFamily="mono">{row.workflow_id}</Text>}
                          </Td>
                          <Td isNumeric>{parseInt(row.total_tokens || 0, 10).toLocaleString()}</Td>
                          <Td isNumeric>{formatNzd(row.cost_usd)}</Td>
                        </Tr>
                      ))}
                      {(usageDetail.recentTokenUsage || []).length === 0 && (
                        <Tr><Td colSpan={5}><Text color="gray.400" textAlign="center" py={3}>No AI calls recorded</Text></Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* ── Edit Credits Modal ─────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Edit Credits — {adjustTarget?.first_name} {adjustTarget?.last_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Summary row */}
              {adjustTarget && (() => {
                const allowance = adjustTarget.credits_monthly_allowance ?? 30;
                const monthlyLeft = adjustTarget.credits_monthly_balance ?? 0;
                const used = Math.max(0, allowance - monthlyLeft);
                const topups = adjustTarget.credits_purchased_balance ?? 0;
                return (
                  <Box bg="gray.50" borderRadius="md" p={3}>
                    <HStack justify="space-between" fontSize="sm">
                      <Text color="gray.500">Allowance</Text><Text fontWeight="semibold">{allowance}</Text>
                    </HStack>
                    <HStack justify="space-between" fontSize="sm">
                      <Text color="gray.500">Used this period</Text><Text fontWeight="semibold" color={used > 0 ? 'orange.500' : 'gray.700'}>{used}</Text>
                    </HStack>
                    <HStack justify="space-between" fontSize="sm">
                      <Text color="gray.500">Monthly left</Text><Text fontWeight="semibold">{monthlyLeft}</Text>
                    </HStack>
                    <HStack justify="space-between" fontSize="sm">
                      <Text color="gray.500">Top-ups</Text><Text fontWeight="semibold" color="green.600">{topups}</Text>
                    </HStack>
                  </Box>
                );
              })()}

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>Monthly balance (remaining credits this period)</Text>
                <Text fontSize="xs" color="gray.400" mb={2}>Used = Allowance − this value. Reduce to reflect more usage, increase to correct an error.</Text>
                <NumberInput
                  value={editMonthlyBalance}
                  onChange={(_, val) => setEditMonthlyBalance(isNaN(val) ? 0 : val)}
                  min={0} max={10000}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>Top-up balance (purchased credits)</Text>
                <Text fontSize="xs" color="gray.400" mb={2}>Never expire. Set to 0 to clear, or increase to grant bonus credits.</Text>
                <NumberInput
                  value={editTopups}
                  onChange={(_, val) => setEditTopups(isNaN(val) ? 0 : val)}
                  min={0} max={10000}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>Reason (required)</Text>
                <Textarea
                  placeholder="e.g. Corrected usage after failed AI call..."
                  value={adjustNotes}
                  onChange={e => setAdjustNotes(e.target.value)}
                  size="sm"
                  rows={2}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button
                colorScheme="brand"
                onClick={handleAdjust}
                isLoading={isAdjusting}
                isDisabled={!adjustNotes.trim()}
              >
                Apply Adjustment
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminCreditManagement;
